export const SUBSCRIPTION_PLAN_ORDER = [
  'trial',
  'premium',
  'pro',
  'obras',
  'construtora',
  'construtora_plus'
] as const;

export type SubscriptionPlanId = typeof SUBSCRIPTION_PLAN_ORDER[number];
export type SubscriptionChangeDirection = 'new' | 'upgrade' | 'downgrade' | 'same';

export interface AppSubscriptionPlan {
  id: Exclude<SubscriptionPlanId, 'trial'>;
  packageAliases: string[];
  name_buttom: string;
  promocao: string;
  info: string;
  name: string;
  storage: string;
  description: string;
}

export const SUBSCRIPTION_PLANS: AppSubscriptionPlan[] = [
  { id: 'premium', packageAliases: ['pro_recorrente_monthly'], name_buttom: 'Assinar', promocao: '', info: 'Para organizar seus primeiros projetos', name: 'Premium', storage: '1 GB', description: 'Assinatura com renovação automática. Cancele quando quiser.' },
  { id: 'pro', packageAliases: ['pro_monthly', 'construfacil_pro_monthly'], name_buttom: 'Assinar', promocao: '', info: 'Mais obras e mais armazenamento', name: 'Pro', storage: '5 GB', description: 'Assinatura com renovação automática. Cancele quando quiser.' },
  { id: 'obras', packageAliases: ['obras_monthly', 'construfacil_obras_monthly'], name_buttom: 'Assinar', promocao: 'Mais escolhido', info: 'Gestão de campo, equipe e acessos', name: 'Obras', storage: '15 GB', description: 'Assinatura com renovação automática. Cancele quando quiser.' },
  { id: 'construtora', packageAliases: ['construtora_monthly', 'construfacil_construtora_monthly'], name_buttom: 'Assinar', promocao: '', info: 'Mais capacidade para sua operação', name: 'Construtora', storage: '25 GB', description: 'Assinatura com renovação automática. Cancele quando quiser.' },
  { id: 'construtora_plus', packageAliases: ['construtora_plus_monthly', 'construfacil_construtora_plus_monthly'], name_buttom: 'Assinar', promocao: 'Maior capacidade', info: 'Capacidade máxima para crescer', name: 'Construtora Plus', storage: '50 GB', description: 'Assinatura com renovação automática. Cancele quando quiser.' }
];

export function normalizeSubscriptionPlan(value: unknown): SubscriptionPlanId | null {
  const normalized = String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  return SUBSCRIPTION_PLAN_ORDER.includes(normalized as SubscriptionPlanId)
    ? normalized as SubscriptionPlanId
    : null;
}

export function subscriptionPlanName(value: unknown): string {
  const planId = normalizeSubscriptionPlan(value);
  if (planId === 'trial') return 'Teste gratuito';
  return SUBSCRIPTION_PLANS.find((plan) => plan.id === planId)?.name || 'Gratuito';
}

export function subscriptionPlanMeetsMinimum(value: unknown, minimum: SubscriptionPlanId): boolean {
  const current = normalizeSubscriptionPlan(value);
  const currentLevel = current ? SUBSCRIPTION_PLAN_ORDER.indexOf(current) : -1;
  const minimumLevel = SUBSCRIPTION_PLAN_ORDER.indexOf(minimum);
  return currentLevel >= minimumLevel && minimumLevel >= 0;
}

export function subscriptionWorkLimit(value: unknown, hasPaidSubscription: boolean): number {
  const planId = normalizeSubscriptionPlan(value);
  if (!hasPaidSubscription || planId === 'trial') return 1;

  const limits: Partial<Record<SubscriptionPlanId, number>> = {
    premium: 2,
    pro: 4,
    obras: 6,
    construtora: 8,
    construtora_plus: 10
  };

  // Assinaturas antigas sem plano identificado seguem a regra do Premium.
  return limits[planId || 'premium'] || 2;
}

export function subscriptionTeamAccessLimit(value: unknown, hasPaidSubscription: boolean): number {
  const planId = normalizeSubscriptionPlan(value);
  if (!hasPaidSubscription) return 0;
  const limits: Partial<Record<SubscriptionPlanId, number>> = {
    obras: 4,
    construtora: 8,
    construtora_plus: 10
  };
  return limits[planId || 'trial'] || 0;
}

export function subscriptionTeamMemberLimit(value: unknown, hasPaidSubscription: boolean): number {
  const planId = normalizeSubscriptionPlan(value);
  if (!hasPaidSubscription) return 0;
  const limits: Partial<Record<SubscriptionPlanId, number>> = {
    obras: 50,
    construtora: 80,
    construtora_plus: 100
  };
  return limits[planId || 'trial'] || 0;
}

export function subscriptionPlanByProduct(productId: unknown): SubscriptionPlanId | null {
  const normalizedProduct = String(productId || '').trim().toLowerCase().split(':')[0];
  return SUBSCRIPTION_PLANS.find((plan) =>
    plan.packageAliases.some((alias) => alias.toLowerCase().split(':')[0] === normalizedProduct)
  )?.id || null;
}

export function entitlementIdByPlan(planId: unknown): string | null {
  const normalized = normalizeSubscriptionPlan(planId);
  const entitlements: Partial<Record<SubscriptionPlanId, string>> = {
    premium: 'premium_access',
    pro: 'pro_access',
    obras: 'obras_access',
    construtora: 'construtora_access',
    construtora_plus: 'construtora_plus_access'
  };
  return normalized ? entitlements[normalized] || null : null;
}

export function getSubscriptionChangeDirection(
  currentPlan: unknown,
  targetPlan: unknown,
  hasActiveSubscription: boolean
): SubscriptionChangeDirection {
  if (!hasActiveSubscription) return 'new';

  const current = normalizeSubscriptionPlan(currentPlan);
  const target = normalizeSubscriptionPlan(targetPlan);
  if (!current || !target) return 'same';

  const currentLevel = SUBSCRIPTION_PLAN_ORDER.indexOf(current);
  const targetLevel = SUBSCRIPTION_PLAN_ORDER.indexOf(target);
  if (targetLevel > currentLevel) return 'upgrade';
  if (targetLevel < currentLevel) return 'downgrade';
  return 'same';
}

export function isSubscriptionPlanDowngrade(currentPlan: unknown, targetPlan: unknown): boolean {
  const current = normalizeSubscriptionPlan(currentPlan);
  const target = normalizeSubscriptionPlan(targetPlan);
  if (!current || current === 'trial' || !target) return false;
  return SUBSCRIPTION_PLAN_ORDER.indexOf(target) < SUBSCRIPTION_PLAN_ORDER.indexOf(current);
}
